import { useEffect, useMemo, useState } from "react";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";
import {
  fetchAdminSettings,
  saveAdminSettings,
} from "@/features/deployment-settings/api/admin-settings-api";
import {
  AVAILABLE_DEPLOYMENT_AREAS,
  cloneAdminSettings,
  getDefaultThresholdUnit,
  getSettingsChangeSummary,
  type AdminSettingsConfiguration,
  type DeploymentAreaCapacity,
  type ThresholdConfig,
  type ThresholdSeverity,
  validateAdminSettings,
} from "@/features/deployment-settings/model/admin-settings";

export type SettingsSaveStatus = "idle" | "saved";

interface AdminSettingsFormState {
  configuration: AdminSettingsConfiguration;
  draft: AdminSettingsConfiguration;
  isEditing: boolean;
  isConfirmationOpen: boolean;
  isSaving: boolean;
  saveStatus: SettingsSaveStatus;
  validationError: string | null;
  errorMessage: string | null;
  isLoading: boolean;
  changes: ReturnType<typeof getSettingsChangeSummary>;
  startEditing: () => void;
  cancelEditing: () => void;
  updateThreshold: (
    configId: string,
    severity: ThresholdSeverity,
    change: Partial<Pick<ThresholdConfig["thresholds"][number], "value" | "unit">>
  ) => void;
  addThreshold: (configId: string, severity: ThresholdSeverity) => void;
  removeThreshold: (configId: string, severity: ThresholdSeverity) => void;
  updateAreaCapacity: (areaId: string, value: number | "") => void;
  addArea: (areaId: string) => void;
  removeArea: (areaId: string) => void;
  requestSave: () => void;
  closeConfirmation: () => void;
  confirmSave: () => Promise<void>;
}

export function useAdminSettingsForm(): AdminSettingsFormState {
  const { deploymentId } = useSelectedDeployment();
  const runtime = useRuntimeHttpClient();
  const [configuration, setConfiguration] = useState<AdminSettingsConfiguration>({
    thresholdConfigs: [],
    areas: [],
  });
  const [draft, setDraft] = useState<AdminSettingsConfiguration>({
    thresholdConfigs: [],
    areas: [],
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SettingsSaveStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (runtime.client === null || runtime.deploymentId !== deploymentId) {
      setIsLoading(true);
      setConfiguration({ thresholdConfigs: [], areas: [] });
      setDraft({ thresholdConfigs: [], areas: [] });
      return;
    }

    let isCurrent = true;
    setIsLoading(true);
    setErrorMessage(null);

    void fetchAdminSettings(deploymentId)
      .then((currentConfiguration) => {
        if (!isCurrent || currentConfiguration == null) {
          return;
        }

        setConfiguration(cloneAdminSettings(currentConfiguration));
        setDraft(cloneAdminSettings(currentConfiguration));
      })
      .catch((error: unknown) => {
        if (isCurrent) {
          setErrorMessage(
            error instanceof Error ? error.message : "Unable to load deployment area settings."
          );
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [deploymentId, runtime.client, runtime.deploymentId]);

  const validationError = useMemo(() => validateAdminSettings(draft), [draft]);
  const changes = useMemo(
    () => getSettingsChangeSummary(configuration, draft),
    [configuration, draft]
  );

  function startEditing() {
    setDraft(cloneAdminSettings(configuration));
    setSaveStatus("idle");
    setErrorMessage(null);
    setIsEditing(true);
  }

  function cancelEditing() {
    if (!isSaving) {
      setDraft(cloneAdminSettings(configuration));
      setIsEditing(false);
    }
  }

  function updateThreshold(
    configId: string,
    severity: ThresholdSeverity,
    change: Partial<Pick<ThresholdConfig["thresholds"][number], "value" | "unit">>
  ) {
    setSaveStatus("idle");
    setDraft((currentDraft) => ({
      ...currentDraft,
      thresholdConfigs: currentDraft.thresholdConfigs.map((config) => {
        if (config.id !== configId) {
          return config;
        }

        return {
          ...config,
          thresholds: config.thresholds.map((threshold) =>
            threshold.severity === severity ? { ...threshold, ...change } : threshold
          ),
        };
      }),
    }));
  }

  function addThreshold(configId: string, severity: ThresholdSeverity) {
    setSaveStatus("idle");
    setDraft((currentDraft) => ({
      ...currentDraft,
      thresholdConfigs: currentDraft.thresholdConfigs.map((config) => {
        if (
          config.id !== configId ||
          !config.availableSeverities.includes(severity) ||
          config.thresholds.some((threshold) => threshold.severity === severity)
        ) {
          return config;
        }

        return {
          ...config,
          thresholds: [
            ...config.thresholds,
            {
              severity,
              value: "",
              unit: getDefaultThresholdUnit(config.availableUnits),
            },
          ],
        };
      }),
    }));
  }

  function removeThreshold(configId: string, severity: ThresholdSeverity) {
    setSaveStatus("idle");
    setDraft((currentDraft) => ({
      ...currentDraft,
      thresholdConfigs: currentDraft.thresholdConfigs.map((config) =>
        config.id === configId
          ? {
              ...config,
              thresholds: config.thresholds.filter((threshold) => threshold.severity !== severity),
            }
          : config
      ),
    }));
  }

  function updateAreaCapacity(areaId: string, value: number | "") {
    setSaveStatus("idle");
    setDraft((currentDraft) => ({
      ...currentDraft,
      areas: currentDraft.areas.map((area) =>
        area.id === areaId ? { ...area, capacity: value } : area
      ),
    }));
  }

  function addArea(areaId: string) {
    setSaveStatus("idle");
    setDraft((currentDraft) => {
      if (currentDraft.areas.some((area) => area.id === areaId)) {
        return currentDraft;
      }

      const areaDefinition = AVAILABLE_DEPLOYMENT_AREAS.find((area) => area.id === areaId);
      if (areaDefinition == null) {
        return currentDraft;
      }

      const area: DeploymentAreaCapacity = { ...areaDefinition, capacity: "" };
      return { ...currentDraft, areas: [...currentDraft.areas, area] };
    });
  }

  function removeArea(areaId: string) {
    setSaveStatus("idle");
    setDraft((currentDraft) => ({
      ...currentDraft,
      areas: currentDraft.areas.filter((area) => area.id !== areaId),
    }));
  }

  function requestSave() {
    if (validationError == null && changes.length > 0) {
      setIsEditing(false);
      setIsConfirmationOpen(true);
    }
  }

  function closeConfirmation() {
    if (!isSaving) {
      setIsConfirmationOpen(false);
      setIsEditing(true);
    }
  }

  async function confirmSave() {
    setIsSaving(true);
    setErrorMessage(null);

    try {
      const savedConfiguration = await saveAdminSettings(deploymentId, draft);
      setConfiguration(cloneAdminSettings(savedConfiguration));
      setDraft(cloneAdminSettings(savedConfiguration));
      setSaveStatus("saved");
      setIsConfirmationOpen(false);
    } catch (error: unknown) {
      setErrorMessage(
        error instanceof Error ? error.message : "Unable to save deployment area settings."
      );
    } finally {
      setIsSaving(false);
    }
  }

  return {
    configuration,
    draft,
    isEditing,
    isConfirmationOpen,
    isSaving,
    saveStatus,
    validationError,
    errorMessage,
    isLoading,
    changes,
    startEditing,
    cancelEditing,
    updateThreshold,
    addThreshold,
    removeThreshold,
    updateAreaCapacity,
    addArea,
    removeArea,
    requestSave,
    closeConfirmation,
    confirmSave,
  };
}
